import React, { useEffect, useRef, useState, forwardRef } from 'react';
import styled from 'styled-components';

/**
 * @alias 기본 이메일 도메인
 */
const DEFAULT_EMAIL_DOMAINS = ['naver.com', 'gmail.com', 'daum.net', 'hanmail.net', 'nate.com', 'kakao.com'];

/**
 * @alias 이메일 입력 Props
 */
interface EmailInputProps {
  /**
   * @alias onChange 이벤트 핸들러
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * @alias onBlur 이벤트 핸들러
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * @alias name 인풋 이름
   */
  name?: string;
  /**
   * @alias placeholder 인풋 플레이스홀더
   */
  placeholder?: string;
  /**
   * @alias disabled 인풋 비활성화 여부
   */
  disabled?: boolean;
  /**
   * @alias value 인풋 값
   */
  value?: string;
  /**
   * @alias defaultValue 인풋 기본값
   */
  defaultValue?: string;
}

/**
 * @alias 이메일 입력 컴포넌트
 * @description 이메일 입력 시 자동완성 기능 제공
 */
const EmailInput = forwardRef<HTMLInputElement, EmailInputProps>(
  ({ onChange, onBlur, name, placeholder = '이메일을 입력하세요', disabled = false, value: controlledValue, defaultValue = '' }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue); // 입력값의 내부 상태 관리
    const [isFocused, setIsFocused] = useState(false); // 인풋 포커스 여부 관리
    const [activeIndex, setActiveIndex] = useState(0); // 추천 리스트에서 현재 선택된 인덱스 관리
    const containerRef = useRef<HTMLDivElement | null>(null); // 전체 컨테이너 div의 ref 관리
    const inputRef = useRef<HTMLInputElement | null>(null); // 인풋 요소의 ref 관리
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]); // 추천 리스트 각 아이템의 ref 배열 관리

    /**
     * @alias ref 통합: 외부 ref와 내부 ref 모두 설정
     */
    useEffect(() => {
      if (typeof ref === 'function') {
        ref(inputRef.current);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
      }
    }, [ref]);

    // 현재 value는 외부에서 props로 받은 값이 있으면 그것을 쓰고, 아니면 내부 상태값을 사용
    const value = controlledValue !== undefined ? controlledValue : internalValue;
    // value에서 '@' 문자의 인덱스를 찾아서 저장
    const atIndex = value.indexOf('@');
    // '@'가 없다면 전체 value를, 있다면 '@' 앞부분을 localPart(이메일 앞부분)로 설정
    const localPart = atIndex === -1 ? value : value.slice(0, atIndex);
    // '@'가 없다면 빈 문자열을, 있다면 '@' 뒤 부분을 domainPart(도메인 부분)으로 설정
    const domainPart = atIndex === -1 ? '' : value.slice(atIndex + 1);
    // domainPart가 있으면 domainPart로 시작하는 도메인만 걸러내고, 없으면 전체 도메인 목록 사용
    const filteredDomains = domainPart ? DEFAULT_EMAIL_DOMAINS.filter((d) => d.toLowerCase().startsWith(domainPart.toLowerCase())) : DEFAULT_EMAIL_DOMAINS;
    // localPart가 존재하고, 필터링된 도메인 목록이 있으면 자동완성 suggestions(추천 이메일 목록) 생성, 아니면 빈 배열
    const suggestions = localPart && filteredDomains.length > 0 ? filteredDomains.map((d) => `${localPart}@${d}`) : [];
    // '@' 뒤에 입력이 없을 때만 자동완성 노출
    const hasTrailingAt = atIndex !== -1 && domainPart === '';
    const showSuggestions = isFocused && suggestions.length > 0 && !disabled && hasTrailingAt;

    /**
     * @alias 입력 이벤트 핸들러
     * @description 입력 시 자동완성 목록 열기 및 값 업데이트
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsFocused(true);

      // input의 값을 업데이트
      if (inputRef.current) {
        inputRef.current.value = e.target.value;
      }

      if (controlledValue === undefined) {
        setInternalValue(e.target.value);
      }

      // React Hook Form의 onChange 호출 (register에서 받은 onChange)
      // 이벤트 객체를 업데이트해서 전달
      const updatedEvent = {
        ...e,
        target: { ...e.target, value: e.target.value },
        currentTarget: { ...e.currentTarget, value: e.target.value },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(updatedEvent);

      setActiveIndex(0);
    };

    /**
     * @alias 선택 이벤트 핸들러
     * @description 선택 시 자동완성 목록 닫기 및 값 업데이트
     */
    const handleSelect = (email: string) => {
      setIsFocused(false); // 선택할 때는 닫음

      if (inputRef.current) {
        // input의 값을 먼저 업데이트
        inputRef.current.value = email;

        if (controlledValue === undefined) {
          setInternalValue(email);
        }

        // React Hook Form의 onChange를 트리거하기 위해 이벤트를 시뮬레이션
        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
      }
    };

    /**
     * @alias 블러 이벤트 핸들러
     * @description React Hook Form의 onBlur 호출
     */
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
    };

    /**
     * @alias 키다운 이벤트 핸들러
     * @description 자동완성 목록 이동 및 선택, 닫기 기능 제공
     *  - ArrowDown: 다음 항목으로 이동
     *  - ArrowUp: 이전 항목으로 이동
     *  - Enter: 선택
     *  - Escape: 목록 닫기
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = activeIndex + 1 >= suggestions.length ? 0 : activeIndex + 1;
          setActiveIndex(nextIndex);
          // 키보드 이동 시 즉시 스크롤 처리
          setTimeout(() => {
            const el = itemRefs.current[nextIndex];
            if (el) {
              el.scrollIntoView({ block: 'nearest' });
            }
          }, 0);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = activeIndex - 1 < 0 ? suggestions.length - 1 : activeIndex - 1;
          setActiveIndex(prevIndex);
          // 키보드 이동 시 즉시 스크롤 처리
          setTimeout(() => {
            const el = itemRefs.current[prevIndex];
            if (el) {
              el.scrollIntoView({ block: 'nearest' });
            }
          }, 0);
          break;
        }
        case 'Enter':
          e.preventDefault();
          const selected = suggestions[activeIndex];
          if (selected) handleSelect(selected);
          break;
        case 'Escape':
          e.preventDefault();
          setIsFocused(false);
          break;
        default:
          break;
      }
    };

    /**
     * @alias 스크롤 이벤트 핸들러
     * @description 키다운으로 목록 이동 시 같이 스크롤
     */
    useEffect(() => {
      if (!showSuggestions) return;
      const el = itemRefs.current[activeIndex];
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }, [activeIndex, showSuggestions]);

    /**
     * @alias 외부 클릭 이벤트 핸들러
     * @description 외부 클릭 시 자동완성 목록 닫기
     */
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsFocused(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <Wrapper ref={containerRef}>
        {/* 입력 인풋 */}
        <Input
          ref={inputRef}
          type="email"
          name={name}
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />

        {/* 자동완성 목록 */}
        {showSuggestions && (
          <SuggestionList>
            {suggestions.map((email, idx) => {
              const [local, domain] = email.split('@');
              return (
                <SuggestionItem
                  key={email}
                  $active={idx === activeIndex}
                  ref={(el) => (itemRefs.current[idx] = el)}
                  onMouseEnter={() => {
                    setActiveIndex(idx);
                    // 마우스 hover 시에도 스크롤 처리
                    setTimeout(() => {
                      const el = itemRefs.current[idx];
                      if (el) {
                        el.scrollIntoView({ block: 'nearest' });
                      }
                    }, 0);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(email);
                  }}
                >
                  <EmailText>
                    <LocalPart>{local}</LocalPart>
                    <AtSign>@</AtSign>
                    <DomainPart>{domain}</DomainPart>
                  </EmailText>
                </SuggestionItem>
              );
            })}
          </SuggestionList>
        )}
      </Wrapper>
    );
  },
);

EmailInput.displayName = 'EmailInput';

export default EmailInput;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding-bottom: 5px;
  border: none;
  background: none;
  border-bottom: 1px solid #ddd;
  font-size: 0.88rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SuggestionList = styled.ul`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;
  margin: 0;
  list-style: none;
  z-index: 10;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.07);
    border-radius: 3px;
  }

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.07) transparent;
`;

const SuggestionItem = styled.li<{ $active: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.88rem;
  background-color: ${({ $active }) => ($active ? '#eff6ff' : 'transparent')};
`;

const EmailText = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
`;

const LocalPart = styled.span`
  color: #111827;
  font-weight: 500;
  font-size: 0.88rem;
`;

const AtSign = styled.span`
  color: #6b7280;
  font-size: 0.88rem;
`;

const DomainPart = styled.span`
  color: #0072ce;
  font-weight: 500;
  font-size: 0.88rem;
`;
